import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PushNotificationType } from '@core/model/pushNotificationType';

export interface NotificationView {
  id: string;
  message: string;
  createdAt?: string;
  pushType?: PushNotificationType;
}

export interface NotificationsDialogData {
  notificationsRef: { list: NotificationView[]; isLoading: boolean; isLoadingMore: boolean; hasMore: boolean };
  onMarkAsReadAndNavigate: (notification: NotificationView) => void;
  onMarkAsReadOnly: (notification: NotificationView, event: Event) => void;
  onNotificationClosed: (notification: NotificationView) => void;
  getIconForType: (type?: PushNotificationType) => string;
  getClassForType: (type?: PushNotificationType) => string;
  onDialogOpened: () => void;
  onLoadMore: () => void;
}

@Component({
  selector: 'app-notifications-dialog',
  templateUrl: './notifications-dialog.component.html',
  styleUrl: './notifications-dialog.component.scss'
})
export class NotificationsDialogComponent implements OnInit {
  constructor(
    public dialogRef: MatDialogRef<NotificationsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: NotificationsDialogData
  ) {}

  ngOnInit(): void {
    this.data.onDialogOpened();
  }

  closeDialog() {
    this.dialogRef.close();
  }

  markAsReadAndNavigate(notification: NotificationView) {
    this.data.onMarkAsReadAndNavigate(notification);
    this.closeDialog();
  }

  markAsReadOnly(notification: NotificationView, event: Event) {
    event.stopPropagation();
    this.data.onMarkAsReadOnly(notification, event);
    this.data.onNotificationClosed(notification);
    this.loadNextPageIfListWasExhausted();
  }

  get shouldShowEmptyState(): boolean {
    const notificationsRef = this.data.notificationsRef;
    return notificationsRef.list.length === 0
      && !notificationsRef.isLoading
      && !notificationsRef.isLoadingMore
      && !notificationsRef.hasMore;
  }

  getIconForType(type?: PushNotificationType): string {
    return this.data.getIconForType(type);
  }

  getClassForType(type?: PushNotificationType): string {
    return this.data.getClassForType(type);
  }

  handleScroll(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const threshold = 64;
    const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
    if (reachedBottom && this.data.notificationsRef.hasMore && !this.data.notificationsRef.isLoadingMore) {
      this.data.onLoadMore();
    }
  }

  private loadNextPageIfListWasExhausted(): void {
    const notificationsRef = this.data.notificationsRef;
    if (notificationsRef.list.length === 0
      && notificationsRef.hasMore
      && !notificationsRef.isLoading
      && !notificationsRef.isLoadingMore) {
      this.data.onLoadMore();
    }
  }
}
